import type { Menu, Page } from '@ps/web-martech-cms-types';
import { useResolver } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useCMSReference } from '@swf/cms';
import type { Props as StarsNavigationItemProps } from '@swf/components/StarsNavigation/StarsNavigationItem';
import StarsNavigationItem from '@swf/components/StarsNavigation/StarsNavigationItem';
import { select } from '@swf/core/store';

import shared from '../../styles/shared.scss';
import { MenuView } from '../../types/MenuView';

import ArrowIcon from './ArrowIcon/ArrowIcon';
import styles from './CMSMenu.scss';
import { CMSMenuGraphContext } from './CMSMenuGraphProvider';

export interface PresentationClassNames {
  internalItem?: string;
  activeItem?: string;
  tagItem?: string;
  arrowButton?: string;
  arrowButtonActive?: string;
  collapsedList?: string;
  expand?: boolean;
}

export interface MenuStructure {
  depth?: number;
  maxDepth?: number;
}

export interface PresentationMarkup {
  tagName?: string;
  onClick?(): void;
}

interface YodaMenu {
  icon?: string;
  icon_active?: string;
}

type Props = { menu: Menu & YodaMenu } & MenuStructure &
  PresentationClassNames &
  PresentationMarkup;

const CMSMenu: React.FC<Props> = ({
  menu: {
    children: submenu,
    page: pageRef,
    heading,
    url,
    resolver_tag: resolverTag,
    external,
    target,
    tag_text: tagText,
    icon,
    icon_active: iconActive
  },
  depth = 0,
  maxDepth = 3,
  internalItem,
  activeItem = '',
  expand = false,
  arrowButton,
  arrowButtonActive = '',
  collapsedList = '',
  tagName,
  tagItem = '',
  onClick
}) => {
  const cmsContent = useSelector(select.cms.getContent);
  const menuView = useSelector(select.cms.getMenuView);
  const graph = useContext(CMSMenuGraphContext);
  const graphNodes = graph.nodes();
  const [rootNode] = graphNodes;
  const { pathname } = useLocation();
  const page = useCMSReference<Page>(pageRef?.[0]);
  const resolverLink = useResolver(resolverTag);
  const href = url || resolverLink || page?.url;
  const withTag = !!tagText;
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useSelector(select.device.isMobile);

  const active = useMemo(
    () =>
      pathname === href ||
      (!!href &&
        !!rootNode &&
        href !== rootNode &&
        ((graphNodes.includes(pathname) && graph.shortestPath(rootNode, pathname).includes(href)) ||
          // Special case for Casino Secondary Navigation items
          // TODO: revisit after ContentStack migration
          (href.split('/').length > 3 && pathname.startsWith(href)))),
    [graph, graphNodes, href, pathname, rootNode]
  );

  const itemProps: StarsNavigationItemProps = {
    className: classNames(internalItem, {
      [activeItem]: !!tagName && active,
      [styles.withTag]: withTag,
      [tagItem]: withTag
    }),
    activeClassName: activeItem,
    isActive: () => active
  };
  const iconUrl = icon && iconActive && (active ? iconActive : icon);

  const [expanded, setExpanded] = useState(expand || active);

  useEffect(() => {
    if (collapsedList) {
      setExpanded(expand || active);
    }
  }, [active, collapsedList, expand]);

  useEffect(() => {
    if (scrollRef.current && active && isMobile) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const getContent = () => {
    if (!iconUrl || menuView === MenuView.text) {
      return <>{heading}</>;
    }

    return (
      <>
        <span className={shared.hideOnScroll}>
          <img src={iconUrl} alt="" />
        </span>
        {menuView === MenuView.iconsAndText && heading}
      </>
    );
  };

  if (external) {
    return (
      <StarsNavigationItem
        tagName="a"
        className={itemProps.className}
        active={active}
        activeClassName={activeItem}
        href={href}
        data-level={depth}
        target={target || undefined}
      >
        {heading}
      </StarsNavigationItem>
    );
  }

  return (
    <>
      {collapsedList && !!submenu.length && depth !== maxDepth - 1 && (
        <ArrowIcon
          className={classNames(arrowButton, {
            [collapsedList]: !expanded,
            [arrowButtonActive]: active
          })}
          onClick={() => setExpanded(!expanded)}
        />
      )}
      <div ref={scrollRef} className={styles.navigationContainer}>
        <StarsNavigationItem
          {...itemProps}
          href={tagName ? undefined : href}
          onClick={() => {
            !!submenu.length && setExpanded(!expanded);
            !submenu.length && onClick && onClick();
          }}
          tagName={href ? tagName : 'span'}
          data-level={depth}
        >
          {withTag && (
            <div className={styles.arrowWrapper}>
              <div className={styles.arrowPointer} />
              <div className={styles.arrowBody}>{tagText}</div>
            </div>
          )}
          {getContent()}
        </StarsNavigationItem>
      </div>
      {!!submenu.length && depth < maxDepth - 1 && (
        <ul
          data-level={depth + 1}
          className={classNames({
            [collapsedList]: !expanded,
            [activeItem]: collapsedList && active
          })}
        >
          {submenu
            .filter((menu) => !!cmsContent[menu.uid])
            .map((menu, index) => (
              <li key={menu.uid} data-level={depth + 1}>
                {tagName && !index && (
                  <>
                    <StarsNavigationItem
                      {...itemProps}
                      href={href}
                      data-level={depth + 1}
                      onClick={onClick}
                    >
                      {heading}
                    </StarsNavigationItem>
                    {collapsedList && (
                      <ArrowIcon
                        className={classNames(arrowButton, { [collapsedList]: !expanded })}
                        onClick={() => setExpanded(!expanded)}
                      />
                    )}
                  </>
                )}
                <CMSMenu
                  menu={cmsContent[menu.uid] as Menu}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  internalItem={internalItem}
                  activeItem={activeItem}
                  arrowButton={arrowButton}
                  collapsedList={collapsedList}
                  onClick={onClick}
                />
              </li>
            ))}
        </ul>
      )}
    </>
  );
};

export default CMSMenu;
